import React from "react";
import { BiPaperPlane } from "react-icons/bi";
import { Link } from "react-router-dom";
import Services from "./Services";
import PortfolioHome from "./PortfolioHome";
import Tools from "./Tools";
import Journey from "./Journey";
import ContactSection from "./ContactSection";
import MarqueeStrip from "./MarqueeStrip";

const ACCENT = "#e83310";

const hexClip = "polygon(50% 1%, 95% 26%, 95% 74%, 50% 99%, 5% 74%, 5% 26%)";

// Abstract animated illustration representing the work: a code editor,
// a live dashboard card, and an orbiting React-style atom.
const WorkVisual = () => (
  <svg
    viewBox="0 0 440 512"
    preserveAspectRatio="xMidYMid slice"
    className="h-full w-full"
    role="img"
    aria-label="Illustration of web development work"
  >
    <defs>
      <linearGradient id="wv-bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#eaf4ff" />
        <stop offset="1" stopColor="#ffe9e2" />
      </linearGradient>
      <linearGradient id="wv-accent" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#211033" />
        <stop offset="1" stopColor="#3d2c58" />
      </linearGradient>
    </defs>

    {/* backdrop */}
    <rect width="440" height="512" fill="url(#wv-bg)" />
    {/* soft blobs */}
    <circle cx="90" cy="120" r="70" fill="#3fb6f0" opacity="0.10" />
    <circle cx="360" cy="400" r="90" fill="#211033" opacity="0.08" />

    {/* floating brackets */}
    <text
      x="96"
      y="180"
      fontFamily="monospace"
      fontSize="34"
      fontWeight="700"
      fill="#211033"
      opacity="0.85"
    >
      {"</>"}
      <animateTransform
        attributeName="transform"
        type="translate"
        values="0 0; 0 -10; 0 0"
        dur="5s"
        repeatCount="indefinite"
      />
    </text>

    {/* dashboard card (floating) */}
    <g>
      <animateTransform
        attributeName="transform"
        type="translate"
        values="0 0; 0 -12; 0 0"
        dur="6s"
        repeatCount="indefinite"
      />
      <rect
        x="248"
        y="150"
        width="118"
        height="84"
        rx="14"
        fill="#ffffff"
        stroke="#e7eef7"
      />
      <rect x="262" y="166" width="46" height="7" rx="3.5" fill="#cbd5e1" />
      {/* bars */}
      <rect x="262" y="196" width="14" height="22" rx="3" fill="#3fb6f0">
        <animate
          attributeName="height"
          values="10;28;10"
          dur="2.4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="208;190;208"
          dur="2.4s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="284" y="188" width="14" height="30" rx="3" fill="#6366f1">
        <animate
          attributeName="height"
          values="30;14;30"
          dur="2.4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="188;204;188"
          dur="2.4s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="306" y="200" width="14" height="18" rx="3" fill="#14b8a6">
        <animate
          attributeName="height"
          values="18;32;18"
          dur="2.4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="200;186;200"
          dur="2.4s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="328" y="194" width="14" height="24" rx="3" fill="#8b5cf6">
        <animate
          attributeName="height"
          values="24;12;24"
          dur="2.4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="194;206;194"
          dur="2.4s"
          repeatCount="indefinite"
        />
      </rect>
    </g>

    {/* main code editor window (gentle float) */}
    <g>
      <animateTransform
        attributeName="transform"
        type="translate"
        values="0 0; 0 8; 0 0"
        dur="7s"
        repeatCount="indefinite"
      />
      <rect
        x="98"
        y="204"
        width="250"
        height="176"
        rx="18"
        fill="#ffffff"
        stroke="#e7eef7"
      />
      {/* title bar dots */}
      <circle cx="120" cy="228" r="5" fill="#ff5f56" />
      <circle cx="138" cy="228" r="5" fill="#ffbd2e" />
      <circle cx="156" cy="228" r="5" fill="#27c93f" />

      {/* animated code lines (typing) */}
      <rect x="120" y="252" height="8" rx="4" fill="#211033">
        <animate
          attributeName="width"
          values="0;96;96;96"
          keyTimes="0;0.3;0.9;1"
          dur="4s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="120" y="272" height="8" rx="4" fill="#c7d2fe">
        <animate
          attributeName="width"
          values="0;0;140;140"
          keyTimes="0;0.3;0.6;1"
          dur="4s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="140" y="292" height="8" rx="4" fill="#bae6fd">
        <animate
          attributeName="width"
          values="0;0;0;110"
          keyTimes="0;0.5;0.7;1"
          dur="4s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="140" y="312" height="8" rx="4" fill="#e2e8f0">
        <animate
          attributeName="width"
          values="0;0;0;80"
          keyTimes="0;0.6;0.85;1"
          dur="4s"
          repeatCount="indefinite"
        />
      </rect>

      {/* run pill */}
      <rect x="120" y="340" width="70" height="24" rx="12" fill="url(#wv-accent)" />
      <polygon points="136,346 136,358 146,352" fill="#ffffff" />
      <rect x="152" y="349" width="28" height="6" rx="3" fill="#ffffff" opacity="0.9" />
    </g>

    {/* orbiting React-style atom */}
    <g>
      <animateTransform
        attributeName="transform"
        type="translate"
        values="0 0; 0 -8; 0 0"
        dur="5.5s"
        repeatCount="indefinite"
      />
      <g transform="translate(132 372)">
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            dur="14s"
            repeatCount="indefinite"
          />
          <ellipse rx="34" ry="13" fill="none" stroke="#14b8a6" strokeWidth="2.5" />
          <ellipse
            rx="34"
            ry="13"
            fill="none"
            stroke="#6366f1"
            strokeWidth="2.5"
            transform="rotate(60)"
          />
          <ellipse
            rx="34"
            ry="13"
            fill="none"
            stroke="#3fb6f0"
            strokeWidth="2.5"
            transform="rotate(120)"
          />
        </g>
        <circle r="6" fill="#211033" />
      </g>
    </g>

    {/* twinkles */}
    <circle cx="300" cy="300" r="4" fill="#211033">
      <animate
        attributeName="opacity"
        values="0.2;1;0.2"
        dur="2s"
        repeatCount="indefinite"
      />
    </circle>
    <circle cx="360" cy="260" r="3" fill="#6366f1">
      <animate
        attributeName="opacity"
        values="1;0.2;1"
        dur="2.6s"
        repeatCount="indefinite"
      />
    </circle>
  </svg>
);

const stats = [
  { value: "15", label: "Projects Delivered" },
  { value: "12", label: "Happy Clients" },
  { value: "6", label: "Years Coding" },
];

// A simple 4-point sparkle
const Sparkle = ({ size = 26, color = "#3fb6f0", className = "", style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    className={className}
    style={style}
    aria-hidden
  >
    <path
      d="M12 0 C13 7 17 11 24 12 C17 13 13 17 12 24 C11 17 7 13 0 12 C7 11 11 7 12 0 Z"
      fill={color}
    />
  </svg>
);

const MainSection = () => {
  return (
    <>
      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden pt-2 sm:pt-4 lg:pt-6">
        <div className="container">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-8">
            {/* Left: copy */}
            <div className="w-full lg:basis-1/2 text-center lg:text-left">
              <p
                className="text-lg sm:text-xl font-medium text-gray-900"
                data-aos="fade-up"
                data-aos-duration="1000"
              >
                Say Hello To{" "}
                <span className="font-semibold" style={{ color: ACCENT }}>
                  Fahim Ferdous
                </span>
              </p>

              <h1
                data-aos="fade-up"
                data-aos-duration="1200"
                className="mt-3 sm:mt-4 font-extrabold text-gray-900 tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
              >
                Full Stack
                <br />
                Developer
              </h1>

              <p
                className="mt-6 text-base sm:text-lg text-gray-500 leading-relaxed max-w-md mx-auto lg:mx-0"
                data-aos="fade-up"
                data-aos-delay="150"
                data-aos-duration="1200"
              >
                Polished front-end interfaces, complete full-stack products, and
                intelligent AI agents &amp; integrations — built to help your
                business grow.
              </p>

              {/* Keyword badges */}
              <div
                className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-2"
                data-aos="fade-up"
                data-aos-delay="200"
                data-aos-duration="1200"
              >
                {[
                  "FrontEnd Development",
                  "Full Stack Development",
                  "AI Agent & Integration",
                ].map((kw) => (
                  <span
                    key={kw}
                    className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-semibold shadow-sm"
                    style={{ color: ACCENT }}
                  >
                    {kw}
                  </span>
                ))}
              </div>

              <div
                className="mt-6"
                data-aos="fade-up"
                data-aos-delay="250"
                data-aos-duration="1200"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-9 py-4 text-sm font-semibold text-white shadow-lg shadow-gray-900/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-black"
                >
                  <BiPaperPlane className="text-lg" />
                  Hire Me
                </Link>
              </div>

              {/* Stats */}
              <div
                className="mt-12 flex items-start justify-center lg:justify-start gap-8 sm:gap-12"
                data-aos="fade-up"
                data-aos-delay="350"
                data-aos-duration="1200"
              >
                {stats.map((s) => (
                  <div key={s.label} className="text-left">
                    <div className="flex items-start text-3xl sm:text-4xl font-extrabold text-gray-900 leading-none">
                      {s.value}
                      <span style={{ color: ACCENT }}>+</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: portrait visual */}
            <div className="w-full lg:basis-1/2 flex justify-center lg:justify-end">
              <div
                className="relative w-[300px] h-[360px] sm:w-[380px] sm:h-[452px] lg:w-[430px] lg:h-[512px]"
                data-aos="zoom-in"
                data-aos-duration="1200"
              >
                {/* Decorative accents */}
                <Sparkle
                  size={34}
                  color="#3fb6f0"
                  className="absolute -left-2 top-10 sm:top-14 z-20"
                  style={{ animation: "hero-float 6s ease-in-out infinite" }}
                />
                <Sparkle
                  size={26}
                  color="#e879b9"
                  className="absolute right-2 -bottom-1 z-20"
                  style={{ animation: "hero-float 5s ease-in-out infinite" }}
                />
                <span className="absolute right-2 top-6 z-20 h-2.5 w-2.5 rounded-full bg-indigo-500" />
                <span className="absolute -left-4 top-1/3 z-20 h-2 w-2 rounded-full bg-sky-500" />
                <span className="absolute -left-1 bottom-24 z-20 h-3 w-3 rounded-full bg-teal-400" />

                {/* Orange outline hexagon (offset) */}
                <svg
                  viewBox="0 0 100 100"
                  className="absolute inset-0 h-full w-full translate-x-[16px] translate-y-[16px] scale-[1.01]"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <polygon
                    points="50,1 95,26 95,74 50,99 5,74 5,26"
                    fill="none"
                    stroke="#f6a97a"
                    strokeWidth="1"
                  />
                </svg>

                {/* Coral filled hexagon (frame) */}
                <div
                  className="absolute inset-0"
                  style={{ clipPath: hexClip, background: "#211033" }}
                />

                {/* Work visual hexagon */}
                <div
                  className="absolute inset-[12px] overflow-hidden"
                  style={{ clipPath: hexClip }}
                >
                  <WorkVisual />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeStrip speedSeconds={40} />
      <Services />
      <PortfolioHome />
      <Tools />
      <Journey />
      <ContactSection />
    </>
  );
};

export default MainSection;
